// ============================================================
// useLoadPhase.ts — the app's NO-LAYOUT-SHIFT loading rule.
//
//   Content must never move once it's on screen because something else
//   loaded. So a late-loading block renders a placeholder with the SAME
//   layout + height as its real content from the first paint, and swaps to
//   the real content in ONE go when everything it needs has arrived.
//
// Phases:
//   'wait'     — not ready, < delayMs since it started waiting: render the
//                placeholder INVISIBLY (it still holds the space), so a fast
//                load never flashes a skeleton.
//   'skeleton' — not ready after delayMs (default 250ms): show the skeleton.
//   'ready'    — render the real content.
//
// Usage:
//   const phase = useLoadPhase(detailsDone && activityDone)
//   phase === 'ready' ? <Real/> : <Skeleton invisible={phase === 'wait'} />
// The skeleton should share the real block's structure (same wrappers, same
// line-height classes) so the swap is height-neutral; see skeletonBar.
// ============================================================
import { useEffect, useState } from 'react'

export type LoadPhase = 'wait' | 'skeleton' | 'ready'

export const useLoadPhase = (ready: boolean, delayMs = 250): LoadPhase => {
  const [late, setLate] = useState(false)
  useEffect(() => {
    if (ready) { setLate(false); return }
    const t = window.setTimeout(() => setLate(true), delayMs)
    return () => window.clearTimeout(t)
  }, [ready, delayMs])
  return ready ? 'ready' : late ? 'skeleton' : 'wait'
}

// The app's skeleton bar (same look as the existing case/dashboard
// skeletons). Inline-block, so placed inside the REAL text wrapper (e.g. the
// value's `text-sm` div) it inherits that line's height exactly.
export const SKELETON_BAR = 'inline-block align-middle rounded-[6px] bg-casevo-border-soft motion-safe:animate-pulse'
